import { Button } from "@material-ui/core";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useState } from "react";
import { PageLoading } from "../UI/Page";
import useStyles from "../shared/Contract/assets/stylesTable";
import { VirtualizedMultiTable } from "../shared/MuiVirtualizedTable";
import MeasurementNavigation from "./MeasurementNavigation";
import { columnsContract, columnsMeasurement } from "./assets/columnsData";

import { EProcessNames } from "api/enums";
import { inputTextToNumber } from "utils";
import { lang } from "../../i18nLang";
import {
  AccessProfileStore,
  ContractRowStore,
  MeasurementRowsStore,
  MessageStore,
} from "../../store";

let _timeoutId;
let calculatingBlock = false; // Utilizada para evitar o delay do useState
const ContractMeasurementTable = observer(() => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [cellError, setCellError] =
    useState<{ rowIndex: number; dataKey: string }>();
  const { currentStage } = MeasurementRowsStore;
  const { rowsCount } = ContractRowStore;

  const updateHasError = MeasurementRowsStore.updateHasFailed();

  const handleChangeValue = (rowIndex, dataKey, value) => {
    if (
      !AccessProfileStore.validateAccessPermissionTo(
        EProcessNames.GERAR_MEDICOES
      )
    ) {
      MessageStore.error(lang("userProfile.profileDoesNotAllow"));
      return;
    }

    // Para evitar que o usuário faça uma alteração em cima de outra.
    if (
      rowIndex === undefined ||
      calculatingBlock ||
      calculating ||
      updateHasError
    ) {
      return;
    }

    value = Number(value);
    if (isNaN(value)) return;

    calculatingBlock = true;
    setCalculating(true);
    MeasurementRowsStore.changeMeasurements(rowIndex, dataKey, value)
      .then((success) => {
        MeasurementRowsStore.setMeasuredColumnTotal(
          MeasurementRowsStore.currentStage
        );
        if (!success) {
          setCellError({ rowIndex, dataKey });
        }
      })
      .finally(() => {
        // Adiciona um timeout para evitar que o usuário consiga calcular
        // mais de 1 linha ao mesmo tempo
        setTimeout(() => {
          calculatingBlock = false;
          setCalculating(false);
          setFocusOnCurrentCellInput();
        }, 100);
      });
  };

  const handleChangeInput = (rowIndex, dataKey, value) => {
    if (typeof value === "string") value = inputTextToNumber(value);
    return value === 0 ? "" : value;
  };

  const handleTableRowGetter = ({ index }) => {
    const rowData: any = MeasurementRowsStore.getRowFormated(index);
    if (!rowData) return {};

    if (rowData.isStage) {
      rowData.customClass = classes.stageRow;
    }

    const isNegative = rowData.isBillingNegative;
    rowData.cellCustoms = {
      description: {
        style: {
          textIndent: `${rowData.level * 0.25}rem`,
        },
      },
      billingQuantity: { isNegative },
      billingPercentual: { isNegative },
      billingTotal: { isNegative },
    };

    if (cellError && cellError.rowIndex === index) {
      rowData.cellCustoms[cellError.dataKey] = {
        hasError: true,
      };
    }

    return rowData;
  };

  const handleChangeStage = (value) => {
    setLoading(true);
    clearTimer();

    _timeoutId = setTimeout(() => {
      MeasurementRowsStore.getRowsListByStage(value)
        .then(() => {
          MeasurementRowsStore.setStage(value);
          MeasurementRowsStore.setMeasuredColumnTotal(
            MeasurementRowsStore.currentStage
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }, 1200);
  };

  columnsMeasurement[0].headComponent = () => (
    <MeasurementNavigation
      stages={MeasurementRowsStore.getStagesNames()}
      currentStage={currentStage}
      onChange={handleChangeStage}
    />
  );

  function setFocusOnCurrentCellInput() {
    const input: any = document.querySelector(
      ".MuiVirtualizedTable-cell-input.focused > input"
    );
    if (input) input.focus();
  }

  return (
    <>
      {loading && (
        <PageLoading
          className={classes.loading}
          title={lang("measurements.loadingStage")}
          actions={() => (
            <Button
              color="primary"
              style={{ marginTop: "0.5rem" }}
              onClick={() => {
                clearTimer();
                setLoading(false);
              }}
            >
              {lang("cancel")}
            </Button>
          )}
        />
      )}
      <VirtualizedMultiTable
        classes={{
          table: clsx(classes.tableMeasurement, {
            loading: calculating,
          }),
        }}
        rowCount={rowsCount}
        rowGetter={handleTableRowGetter}
        rowHeight={34}
        headCellClassName={classes.tableHeadCell}
        cellClassName={classes.tableCell}
        columnsLeft={columnsContract}
        columnsRight={columnsMeasurement}
        onEnableInputCell={() => setCellError(undefined)}
        onChangeInputCell={handleChangeInput}
        onChangeInputCellValue={handleChangeValue}
        disableInput={loading || calculating || updateHasError}
      />
    </>
  );
});

function clearTimer() {
  if (_timeoutId) clearTimeout(_timeoutId);
}

export default ContractMeasurementTable;
